import type { Contact } from 'src/services/contacts/contacts.types';

export interface OrgNode {
  contact: Contact;
  children: OrgNode[];
}

/**
 * Build a forest of reporting trees from the flat company contact list.
 * Handled gracefully: no-manager roots; a manager pointing outside this company or to a
 * deleted contact (dangling) -> root; self-reference -> root; cycles -> first member
 * encountered is promoted to a root so the whole subtree still renders.
 */
export function buildOrgForest(contacts: Contact[]): OrgNode[] {
  const byId = new Map<string, Contact>(contacts.map((c) => [c.id, c]));
  const nodeById = new Map<string, OrgNode>(contacts.map((c) => [c.id, { contact: c, children: [] }]));

  const resolvesToRoot = (c: Contact): boolean => {
    const managerId = c.reports_to_contact_id;
    return !managerId || managerId === c.id || !byId.has(managerId);
  };

  const isInCycle = (c: Contact): boolean => {
    const seen = new Set<string>();
    let cursor: Contact | undefined = c;
    while (cursor && cursor.reports_to_contact_id && byId.has(cursor.reports_to_contact_id)) {
      if (seen.has(cursor.id)) return true;
      seen.add(cursor.id);
      cursor = byId.get(cursor.reports_to_contact_id);
    }
    return false;
  };

  const roots: OrgNode[] = [];
  contacts.forEach((c) => {
    const node = nodeById.get(c.id)!;
    if (resolvesToRoot(c) || isInCycle(c)) {
      roots.push(node);
      return;
    }
    nodeById.get(c.reports_to_contact_id!)!.children.push(node);
  });
  return roots;
}

/** Depth-first lookup of a node by contact id within a forest. */
export function findNode(forest: OrgNode[], contactId: string): OrgNode | null {
  let found: OrgNode | null = null;
  forest.some((node) => {
    if (node.contact.id === contactId) {
      found = node;
      return true;
    }
    const inChild = findNode(node.children, contactId);
    if (inChild) {
      found = inChild;
      return true;
    }
    return false;
  });
  return found;
}

/** Ids of a node's entire subtree — used to forbid picking a descendant as manager. */
export function collectDescendantIds(node: OrgNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: OrgNode) => n.children.forEach((child) => { ids.add(child.contact.id); walk(child); });
  walk(node);
  return ids;
}
